import { Example } from "../models/ExampleModel"
import { ExplanationModel } from "../models/ExplanationModel"
import { ConjugationTableData } from "../models/table-models/ConjugationTableDataModel"
import { VerbConjugation } from "../models/VerbConjugationModel"

export const subjunctiveTopicDescription: ExplanationModel = {
    title: "📖 What is Subjuntivo?",
    description: "The subjunctive is used for expressing subjectivity, doubt, desires, emotions, and hypothetical situations.",
    examples: [
        "✅ Uncertainty: No creo que él venga hoy. (I don't think he is coming today.)",
        "✅ Desire: Espero que tú estudies para el examen. (I hope that you study for the exam.)",
        "✅ Hypothetical: Si tuviera dinero, viajaría por el mundo. (If I had money, I would travel the world.)"
    ]
}

export const subjunctiveUsageInfo: ExplanationModel = {
    title: "When is the Present Subjunctive Used?",
    description: "The subjunctive is used in the following situations:",
    bulletPoints: [
        "🌟 Wishes & Desires: Quiero que vengas a la fiesta. → I want you to come to the party.",
        "🤔 Doubt & Uncertainty: No creo que sea posible. → I don’t think it’s possible.",
        "📅 Future Actions (when uncertain): Cuando tenga tiempo, te llamaré. → When I have time, I’ll call you.",
        "📝 Impersonal Expressions: Es importante que estudies. → It’s important that you study.",
        "🔗 Subordinate Clauses with 'que': Espero que puedas venir. → I hope you can come.",
        "🙏 Ojalá + Subjunctive: ¡Ojalá haga buen tiempo mañana! → Hopefully the weather will be nice tomorrow!"
    ]
}

export const subjunctiveStory = `
    Friend 1: 'Ojalá que no llueva este fin de semana. Quiero ir a la playa.'
    Friend 2: 'Sí, yo también. Es posible que tengamos buen clima si miramos el pronóstico.'
    Friend 1: 'Espero que no haya mucha gente. Me gustaría estar tranquilos.'
    Friend 2: 'Es mejor que lleguemos temprano si no queremos que se llene.'
    Friend 1: 'Tal vez debería llamar a Ana para ver si quiere unirse.'
    Friend 2: '¡Buena idea! Ojalá pueda venir, pero no estoy seguro de que tenga tiempo.'
    Friend 1: 'Bueno, si no puede, igual pasaremos un buen rato. Espero que todo salga bien.'
    ㅤㅤㅤㅤㅤㅤㅤ
    📌 Notice how the subjunctive is used to express wishes, doubts, and recommendations:
    - 'Ojalá que no llueva' (I hope it doesn’t rain)
    - 'Es posible que tengamos buen clima' (It’s possible we’ll have good weather)
    - 'Es mejor que lleguemos temprano' (It’s better that we arrive early)
    - 'No estoy seguro de que tenga tiempo' (I’m not sure that she has time)
    - 'Espero que todo salga bien' (I hope everything goes well)
`;


export const PreteriteSubjunctiveExplanation: ExplanationModel = {
    title: "When is the Past Subjunctive Used?",
    description: "Used for past actions with high uncertainty, hypothetical scenarios, or politeness. Generally combined with conditional statements.",
    examples: [
        "Si tuviera más tiempo, viajaría por el mundo.",
        "Quería que vinieras a la fiesta."
    ]
}

export const subjunctiveIdentification: ExplanationModel = {
    title: "How to Recognize the Present Subjunctive?",
    description: "You can often recognize the subjunctive by:",
    bulletPoints: [
        "✅ Subordinate Clauses: Sentences with 'que' (Espero que, Dudo que).",
        "🙏 Expressions with 'Ojalá': Ojalá no llueva mañana. / Ojalá no lloviera ayer.",
        "🗣️ Verbs Expressing Desire, Doubt, Emotion: Querer, Esperar, Temer, Dudar.",
        "❓ Uncertainty in the Future: Cuando tenga tiempo, te ayudaré. / Cuando tuviera tiempo, te ayudaba."
    ]
}

export const exampleIntro = "The subjunctive is often used when expressing wishes, doubts, emotions, or hypothetical situations. Here are some examples:"

export const SubjunctiveExamplesData = {
    presentExamples: [
        new Example(
            "Deseo que tengas un buen día.",
            "Used to express a wish or desire – 'I wish you have a good day.'"
        ),

        new Example(
            "Dudo que él diga la verdad.",
            "Expresses doubt or uncertainty – 'I doubt that he is telling the truth.'"
        ),

        new Example(
            "Es posible que llueva mañana.",
            "Shows uncertainty about the future – 'It’s possible that it will rain tomorrow.'"
        ),
        new Example(
            "Cuando termine el trabajo, saldré a caminar.",
            "Indicates a future action with uncertainty – 'When I finish work, I will go for a walk.'"
        ),
        new Example(
            "Me alegra que estés aquí.",
            "Expresses emotion or sentiment – 'I'm happy that you're here.'"
        ),
        new Example(
            "Ojalá podamos viajar el próximo año.",
            "Uses 'ojalá' to express hope – 'Hopefully, we can travel next year.'"
        )
    ],

    preteriteExamples: [
        new Example(
            "Si tuviera más dinero, compraría una casa.",
            "This is a hypothetical condition."
        ),
        new Example(
            "Quería que me ayudaras con el proyecto.",
            "After 'quería que', we use the imperfect subjunctive."
        ),
        new Example(
            "Esperaba que él viniera a la fiesta.",
            "The main verb esperaba is in the past, so viniera is used."
        ),
        new Example(
            "Dudaba que ella lo hiciera.",
            "Since dudaba is past, hiciera is used instead of haga."
        ),
        new Example(
            "Si tuviera dinero, viajaría por el mundo.",
            "The past subjunctive tuviera is used in hypothetical conditional sentences."
        ),
        new Example(
            "Ojalá ganara la lotería.",
            "Past subjunctive is used to express unlikely wishes."
        ),
        new Example(
            "Me sorprendió que dijeras eso.",
            "The past subjunctive dijeras follows 'Me sorprendió que...'"
        )
    ]
}

export const regularVerbsTableData: ConjugationTableData = new ConjugationTableData(
    "Regular Verbs in the Present Subjunctive",
    [
        new VerbConjugation("Hablar", ["hable", "hables", "hable", "hablemos", "habléis", "hablen"]),
        new VerbConjugation("Leer", ["lea", "leas", "lea", "leamos", "leáis", "lean"]),
        new VerbConjugation("Vivir", ["viva", "vivas", "viva", "vivamos", "viváis", "vivan"])
    ]
)

export const regularVerbsTableDataPast: ConjugationTableData = new ConjugationTableData(
    "Regular Past Subjunctive Conjugation",
    [
        new VerbConjugation("Hablar", [
            "hablara / hablase",
            "hablaras / hablases",
            "hablara / hablase",
            "habláramos / hablásemos",
            "hablarais / hablaseis",
            "hablaran / hablasen"
        ]),
        new VerbConjugation("Comer", [
            "comiera / comiese",
            "comieras / comieses",
            "comiera / comiese",
            "comiéramos / comiésemos",
            "comierais / comieseis",
            "comieran / comiesen"
        ]),
        new VerbConjugation("Vivir", [
            "viviera / viviese",
            "vivieras / vivieses",
            "viviera / viviese",
            "viviéramos / viviésemos",
            "vivierais / vivieseis",
            "vivieran / viviesen"
        ])
    ]
);

export const irregularVerbsTableDataPast: ConjugationTableData = new ConjugationTableData(
    "Irregular Past Subjunctive Conjugation",
    [
        new VerbConjugation("Ser / Ir", [
            "fuera / fuese",
            "fueras / fueses",
            "fuera / fuese",
            "fuéramos / fuésemos",
            "fuerais / fueseis",
            "fueran / fuesen"
        ]),
        new VerbConjugation("Tener", [
            "tuviera / tuviese",
            "tuvieras / tuvieses",
            "tuviera / tuviese",
            "tuviéramos / tuviésemos",
            "tuvierais / tuvieseis",
            "tuvieran / tuviesen"
        ]),
        new VerbConjugation("Decir", [
            "dijera / dijese",
            "dijeras / dijeses",
            "dijera / dijese",
            "dijéramos / dijésemos",
            "dijerais / dijeseis",
            "dijeran / dijesen"
        ]),
        new VerbConjugation("Hacer", [
            "hiciera / hiciese",
            "hicieras / hicieses",
            "hiciera / hiciese",
            "hiciéramos / hiciésemos",
            "hicierais / hicieseis",
            "hicieran / hiciesen"
        ]),
        new VerbConjugation("Poder", [
            "pudiera / pudiese",
            "pudieras / pudieses",
            "pudiera / pudiese",
            "pudiéramos / pudiésemos",
            "pudierais / pudieseis",
            "pudieran / pudiesen"
        ]),
        new VerbConjugation("Venir", [
            "viniera / viniese",
            "vinieras / vinieses",
            "viniera / viniese",
            "viniéramos / viniésemos",
            "vinierais / vinieseis",
            "vinieran / viniesen"
        ]),
        new VerbConjugation("Poner", [
            "pusiera / pusiese",
            "pusieras / pusieses",
            "pusiera / pusiese",
            "pusiéramos / pusiésemos",
            "pusierais / pusieseis",
            "pusieran / pusiesen"
        ]),
        new VerbConjugation("Saber", [
            "supiera / supiese",
            "supieras / supieses",
            "supiera / supiese",
            "supiéramos / supiésemos",
            "supierais / supieseis",
            "supieran / supiesen"
        ]),
        new VerbConjugation("Querer", [
            "quisiera / quisiese",
            "quisieras / quisieses",
            "quisiera / quisiese",
            "quisiéramos / quisiésemos",
            "quisierais / quisieseis",
            "quisieran / quisiesen"
        ])
    ]
);



export const irregularVerbsSubjunctivo: ConjugationTableData = new ConjugationTableData(
    "Present Irregular Stem-Changing Verbs",
    [
        new VerbConjugation("Pensar (e → ie)", ["piense", "pienses", "piense", "pensemos", "penséis", "piensen"]),
        new VerbConjugation("Pedir (e → i)", ["pida", "pidas", "pida", "pidamos", "pidáis", "pidan"]),
        new VerbConjugation("Poder (o → ue)", ["pueda", "puedas", "pueda", "podamos", "podáis", "puedan"]),
        new VerbConjugation("Jugar (u → ue)", ["juegue", "juegues", "juegue", "juguemos", "juguéis", "jueguen"]),
        new VerbConjugation("Mentir (e → i)", ["mienta", "mientas", "mienta", "mintamos", "mintáis", "mientan"]),
        new VerbConjugation("Dormir (o → u)", ["duerma", "duermas", "duerma", "durmamos", "durmáis", "duerman"])
    ]
)

export const semiIrregularVerbsSubjunctivo: ConjugationTableData = new ConjugationTableData(
    "Present Semi-Irregular Verbs",
    [
        new VerbConjugation("Conocer", ["conozca", "conozcas", "conozca", "conozcamos", "conozcáis", "conozcan"]),
        new VerbConjugation("Hacer", ["haga", "hagas", "haga", "hagamos", "hagáis", "hagan"]),
        new VerbConjugation("Tener", ["tenga", "tengas", "tenga", "tengamos", "tengáis", "tengan"]),
        new VerbConjugation("Salir", ["salga", "salgas", "salga", "salgamos", "salgáis", "salgan"]),
        new VerbConjugation("Decir", ["diga", "digas", "diga", "digamos", "digáis", "digan"]),
        new VerbConjugation("Traducir", ["traduzca", "traduzcas", "traduzca", "traduzcamos", "traduzcáis", "traduzcan"]),
        new VerbConjugation("Venir", ["venga", "vengas", "venga", "vengamos", "vengáis", "vengan"]),
        new VerbConjugation("Traer", ["traiga", "traigas", "traiga", "traigamos", "traigáis", "traigan"]),
        new VerbConjugation("Poner", ["ponga", "pongas", "ponga", "pongamos", "pongáis", "pongan"])
    ]
)

export const totalIrregularVerbsSubjunctivo: ConjugationTableData = new ConjugationTableData(
    "Present Common Irregular Verbs",
    [
        new VerbConjugation("Ser", ["sea", "seas", "sea", "seamos", "seáis", "sean"]),
        new VerbConjugation("Estar", ["esté", "estés", "esté", "estemos", "estéis", "estén"]),
        new VerbConjugation("Ver", ["vea", "veas", "vea", "veamos", "veáis", "vean"]),
        new VerbConjugation("Haber", ["haya", "hayas", "haya", "hayamos", "hayáis", "hayan"]),
        new VerbConjugation("Saber", ["sepa", "sepas", "sepa", "sepamos", "sepáis", "sepan"]),
        new VerbConjugation("Querer", ["quiera", "quieras", "quiera", "queramos", "queráis", "quieran"]),
        new VerbConjugation("Llegar", ["llegue", "llegues", "llegue", "lleguemos", "lleguéis", "lleguen"])
    ]
)


export const PastPerfectSubjunctiveExplanation: ExplanationModel = {
    title: "Pluscuamperfecto de Subjuntivo",
    description: "The past perfect subjunctive is used to describe hypothetical or unreal situations in the past. It is formed using 'haber' in the past subjunctive + past participle.",
    bulletPoints: [
        "✅ Used in conditional sentences: Si hubiera sabido, habría venido. → If I had known, I would have come.",
        "🙏 Expresses wishes or regrets about the past: Ojalá hubiera estudiado más. → I wish I had studied more.",
        "🗣️ Often used in reported speech: Esperaba que hubieras llegado a tiempo. → I was hoping you had arrived on time."
    ]
}

export const PastPerfectSubjunctiveExamples: Example[] = [
    new Example("Si tú hubieras venido, nos habríamos divertido.", "If you had come, we would have had fun."),
    new Example("Ojalá hubiese estudiado más para el examen.", "I wish I had studied more for the exam."),
    new Example("Esperaba que ellos hubieran llegado antes.", "I was hoping that they had arrived earlier.")
]

export const pastPerfectSubjunctiveRegular: ConjugationTableData = new ConjugationTableData(
    "Regular Verbs",
    [
        new VerbConjugation("Hablar", ["hubiera hablado", "hubieras hablado", "hubiera hablado", "hubiéramos hablado", "hubierais hablado", "hubieran hablado"]),
        new VerbConjugation("Comer", ["hubiera comido", "hubieras comido", "hubiera comido", "hubiéramos comido", "hubierais comido", "hubieran comido"]),
        new VerbConjugation("Vivir", ["hubiera vivido", "hubieras vivido", "hubiera vivido", "hubiéramos vivido", "hubierais vivido", "hubieran vivido"])
    ]
)

export const pastPerfectSubjunctiveIrregular: ConjugationTableData = new ConjugationTableData(
    "Irregular Verbs",
    [
        new VerbConjugation("Hacer", ["hubiera hecho", "hubieras hecho", "hubiera hecho", "hubiéramos hecho", "hubierais hecho", "hubieran hecho"]),
        new VerbConjugation("Decir", ["hubiera dicho", "hubieras dicho", "hubiera dicho", "hubiéramos dicho", "hubierais dicho", "hubieran dicho"]),
        new VerbConjugation("Poner", ["hubiera puesto", "hubieras puesto", "hubiera puesto", "hubiéramos puesto", "hubierais puesto", "hubieran puesto"]),
        new VerbConjugation("Salir", ["hubiera salido", "hubieras salido", "hubiera salido", "hubiéramos salido", "hubierais salido", "hubieran salido"]),
        new VerbConjugation("Poder", ["hubiera podido", "hubieras podido", "hubiera podido", "hubiéramos podido", "hubierais podido", "hubieran podido"]),
        new VerbConjugation("Saber", ["hubiera sabido", "hubieras sabido", "hubiera sabido", "hubiéramos sabido", "hubierais sabido", "hubieran sabido"]),
        new VerbConjugation("Haber", ["hubiera habido", "hubieras habido", "hubiera habido", "hubiéramos habido", "hubierais habido", "hubieran habido"])
    ]
)

export const pastSubjunctiveStoryTrip = `
Amigo 1: 'Me gustaría que el año pasado fuera más fácil viajar a la montaña. Tenía muchísimas ganas de ir'.
Amigo 2: 'Sí, yo también. Podríamos haber tenido un gran viaje si no hubiera sido tan caro.'
Amigo 1: 'Ojalá hubiera tenido más tiempo. Si tuviera tiempo ahora, iría en un abrir y cerrar de ojos.
Amigo 2: 'Era mejor si nos organizáramos con anticipación si queríamos hacer todo lo que queríamos'.
Amigo 1: 'Tal vez debería haber hecho más planes de antemano. Ojalá tuviera más energía.'
Amigo 2: 'Si nos hubiéramos preparado mejor, hubiéramos tenido una experiencia increíble. ¡Qué pena!'
ㅤㅤㅤㅤㅤㅤㅤ
   📌 Key Takeaways: 
    - The past imperfect subjunctive reflects on past hypothetical situations or advice that could have changed the outcome. (fuera más fácil viajar)
    - The past perfect subjunctive is used to express regrets or wishes about past actions that didn’t happen. ('Ojalá hubiera tenido más tiempo.')
    - The conditional perfect discusses a hypothetical scenario in the past that did not occur but could have if conditions were different. (Podríamos haber tenido un gran viaje)
`;
